import { useLiveQuery } from 'dexie-react-hooks';
import { listTrash, restoreDocument, purgeDocument, emptyTrash } from '../lib/library';
import { useStore } from '../store/useStore';
import { useT } from '../i18n/useT';
import { Modal } from './Modal';
import { askConfirm } from '../lib/dialogs';
import { formatDateTime, formatRelative } from '../lib/format';

/**
 * Browsable trash: lists soft-deleted documents (backed by `listTrash()` via a
 * live query) and lets the user restore them or delete them permanently. The
 * immediate post-delete UndoToast handles the common case; this dialog is for
 * recovering something deleted a while ago.
 */
export function TrashDialog() {
  const t = useT();
  const open = useStore((s) => s.trashOpen);
  const setOpen = useStore((s) => s.setTrashOpen);
  const docs = useLiveQuery(() => (open ? listTrash() : Promise.resolve([])), [open], []);

  if (!open) return null;
  const close = () => setOpen(false);

  return (
    <Modal onClose={close} title={t('trash.title')} className="mb-modal-wide">
      <p className="mb-modal-note">{t('trash.note')}</p>

      {docs.length === 0 ? (
        <p className="mb-modal-note">{t('trash.empty')}</p>
      ) : (
        <ul className="mb-share-list">
          {docs.map((doc) => (
            <li key={doc.id} className="mb-share-row">
              <div className="mb-share-row-main">
                <span className="mb-share-row-title">{doc.title || t('common.untitled')}</span>
                {doc.deletedAt != null && (
                  <span className="mb-share-expiry" title={formatDateTime(doc.deletedAt)}>
                    {t('trash.deletedAt', { date: formatRelative(doc.deletedAt) })}
                  </span>
                )}
              </div>
              <div className="mb-share-row-actions">
                <button className="mb-btn" onClick={() => void restoreDocument(doc.id)}>
                  {t('trash.restore')}
                </button>
                <button
                  className="mb-btn mb-menu-danger"
                  onClick={async () => {
                    const ok = await askConfirm({
                      title: t('trash.deleteForever'),
                      body: t('trash.deleteConfirm', {
                        title: doc.title || t('common.untitled'),
                      }),
                      confirmLabel: t('trash.deleteForever'),
                      danger: true,
                    });
                    if (ok) void purgeDocument(doc.id);
                  }}
                >
                  {t('trash.deleteForever')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="mb-modal-actions">
        {docs.length > 0 && (
          <button
            className="mb-btn mb-menu-danger"
            onClick={async () => {
              const ok = await askConfirm({
                title: t('trash.emptyAll'),
                body: t('trash.emptyConfirm'),
                confirmLabel: t('trash.emptyAll'),
                danger: true,
              });
              if (ok) void emptyTrash();
            }}
          >
            {t('trash.emptyAll')}
          </button>
        )}
        <button className="mb-btn mb-btn-primary" onClick={close}>
          {t('common.done')}
        </button>
      </div>
    </Modal>
  );
}
